import type { Property, SearchPropertyParams, SearchPropertyResponse } from '@/types/Property'
import type {
  DocumentDto,
  MainNumberDto,
  OwnershipType,
  PropertyDetailsDto,
} from '@/types/PropertyDto'
import type { PropertySummaryDto } from '@/types/PropertySummary'
import { getCurrentTenant } from '@/utils/tenant-utils'
import {
  ApiClient,
  ApiError,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  type WithEtag,
} from './ApiClient'

// Type for the actual API response structure. `totalArea` and every `areaDistributions[].area`
// are whole decares (daa), already rounded by the backend.
interface ApiPropertyResponse {
  propertyId: {
    municipalityNumber: number
    farmNumber: number
    holdingNumber: number
    leaseNumber: number
  }
  properties: {
    propertyId: {
      municipalityNumber: number
      farmNumber: number
      holdingNumber: number
      leaseNumber: number
    }
    areaDistributions: {
      areaType: number
      area: number
    }[]
    totalArea: number
  }[]
}

export interface PropertySearchParams {
  municipality?: string
  farmNumber?: string
  propertyNumber?: string
  advancedSearch?: string
  sort?: string
  pageSize?: number
  continuationToken?: string
  includeExternal?: boolean
}

export interface PropertySearchResponse {
  properties: PropertyDetailsDto[]
  continuationToken: string | undefined
  hasMore: boolean
}

export async function searchProperty(
  params: SearchPropertyParams,
): Promise<SearchPropertyResponse> {
  const response = await ApiClient.get<ApiPropertyResponse>('/external/properties', {
    params: {
      // Backend expects municipalityNumber and holdingNumber
      municipalityNumber: params.municipality,
      farmNumber: params.farmNumber,
      holdingNumber: params.propertyNumber,
    },
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const hasMoreResultsHeader = response.headers.get('x-has-more-results')?.trim().toLowerCase()
  const hasMore = hasMoreResultsHeader === 'true'

  // Get the HNR from the top-level propertyId
  const hnr = response.data.propertyId
  const hnrMunicipalityNumber = hnr.municipalityNumber.toString()

  // Create the HNR property (parent property) - minimal data since it's only used for hnr reference
  const hnrProperty: Property = {
    municipality: {
      name: params.municipalityName ?? '', // Use provided municipality name or empty string
      number: hnrMunicipalityNumber,
    },
    farmNumber: hnr.farmNumber.toString(),
    propertyNumber: hnr.holdingNumber.toString(),
    leaseNumber: hnr.leaseNumber.toString(),
    mainNumber: `${hnrMunicipalityNumber}-${hnr.farmNumber}/${hnr.holdingNumber}`,
    property: `${hnr.farmNumber}/${hnr.holdingNumber}`,
    name: `Hovednummer ${hnr.farmNumber}/${hnr.holdingNumber}`,
    area: 0,
    shares: 100,
    bonitet: {
      class: 1,
      siteIndex: 1,
      description: '',
    },
    hnr: hnr,
    isHnr: true,
  }

  // Transform individual properties (GBNRs) under this HNR
  const childProperties: Property[] = response.data.properties.map((apiProperty) => {
    const propertyId = apiProperty.propertyId
    const municipalityNumber = propertyId.municipalityNumber.toString()
    const farmNumber = propertyId.farmNumber.toString()
    const holdingNumber = propertyId.holdingNumber.toString()
    const leaseNumber = propertyId.leaseNumber.toString()
    // Two properties can share the same holding number (bnr) but differ by lease number
    // (festenummer), e.g. 33/8 and 33/8/1. The identity strings must include the lease
    // number whenever it is set, otherwise these distinct properties collide and one of
    // them gets silently dropped as a "duplicate" during grouping.
    const leaseSuffix = leaseNumber !== '0' ? `/${leaseNumber}` : ''

    return {
      municipality: {
        name: params.municipalityName ?? '', // Use provided municipality name or empty string
        number: municipalityNumber,
      },
      farmNumber: farmNumber,
      propertyNumber: holdingNumber,
      leaseNumber,
      mainNumber: `${municipalityNumber}-${farmNumber}/${holdingNumber}${leaseSuffix}`,
      property: `${farmNumber}/${holdingNumber}${leaseSuffix}`,
      name: `Gård ${farmNumber} Bruk ${holdingNumber}${leaseSuffix ? ` Feste ${leaseNumber}` : ''}`,
      area: apiProperty.totalArea,
      shares: 0, // Not available in API - will be set during ownership validation
      bonitet: {
        class: 0, // Not available in API - will be set during ownership validation
        siteIndex: 0,
        description: '',
      },
      bonitetDistribution: {
        HighQuality: {
          size: apiProperty.areaDistributions
            .filter((d) => d.areaType === 4 || d.areaType === 5)
            .reduce((sum, d) => sum + d.area, 0),
          factor: 0,
          shares: 0,
          protectedArea: 0,
        },
        MediumQuality: {
          size: apiProperty.areaDistributions
            .filter((d) => d.areaType === 6)
            .reduce((sum, d) => sum + d.area, 0),
          factor: 0,
          shares: 0,
          protectedArea: 0,
        },
        LowQuality: {
          size: apiProperty.areaDistributions
            .filter((d) => d.areaType === 7)
            .reduce((sum, d) => sum + d.area, 0),
          factor: 0,
          shares: 0,
          protectedArea: 0,
        },
        TotalSize: apiProperty.areaDistributions
          .filter((d) => d.areaType >= 4 && d.areaType <= 7)
          .reduce((sum, d) => sum + d.area, 0),
      },
      hnr: hnr,
      isHnr: false,
    }
  })

  // Return HNR property first, followed by child properties
  const properties: Property[] = [hnrProperty, ...childProperties]

  return {
    properties,
    hasMore,
  }
}

export async function searchProperties(
  params: PropertySearchParams,
): Promise<PropertySearchResponse> {
  const queryParams: Record<string, string> = {}

  if (params.municipality) {
    queryParams.municipalityNumber = params.municipality
  }
  if (params.farmNumber) {
    queryParams.farmNumber = params.farmNumber
  }
  if (params.propertyNumber) {
    queryParams.holdingNumber = params.propertyNumber
  }
  if (params.advancedSearch) {
    queryParams.searchTerm = params.advancedSearch
  }
  if (params.sort) {
    queryParams.sort = params.sort
  }
  if (params.includeExternal !== undefined) {
    queryParams.includeExternal = String(params.includeExternal)
  }

  const response = await ApiClient.get<PropertyDetailsDto[]>('/properties', {
    headers: {
      'x-page-size': params.pageSize?.toString() ?? '50',
      ...(params.continuationToken ? { 'x-continuation-token': params.continuationToken } : {}),
    },
    params: queryParams,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    properties: response.data,
    continuationToken: continuationToken ?? undefined,
    hasMore: hasMoreResults,
  }
}

export interface GetPropertiesForForestOwnerParams {
  forestOwnerId: string
  filterCapitalContributing?: boolean
}

export async function getPropertiesForForestOwner(
  params: GetPropertiesForForestOwnerParams,
): Promise<PropertySummaryDto> {
  const { forestOwnerId, filterCapitalContributing } = params
  const queryParams = filterCapitalContributing ? { filterCapitalContributing: true } : undefined

  const response = await ApiClient.get<PropertySummaryDto>(
    `/forestowners/${forestOwnerId}/properties`,
    {
      params: queryParams,
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export async function getProperty(propertyId: string): Promise<WithEtag<PropertyDetailsDto>> {
  return apiGet<PropertyDetailsDto>(`/properties/${propertyId}`)
}

export async function deletePropertyOwnership(
  forestOwnerId: string,
  propertyId: string,
  ownershipId: string,
  etag?: string | null,
): Promise<void> {
  await apiDelete(
    `/forestowners/${forestOwnerId}/properties/${propertyId}/ownerships/${ownershipId}`,
    {
      headers: etag ? { 'If-Match': etag } : undefined,
    },
  )
}

export interface HistoricalPropertyDto {
  propertyId: string
  cadastralNumber: string
  ownershipRemovedAt: string
  ownershipType: string
}

export interface GetHistoricalPropertiesParams {
  forestOwnerId: string
  pageSize?: number
  continuationToken?: string
}

export interface HistoricalPropertiesResponse {
  entities: HistoricalPropertyDto[]
  hasMore: boolean
  continuationToken?: string
}

export async function getHistoricalProperties(
  params: GetHistoricalPropertiesParams,
): Promise<HistoricalPropertiesResponse> {
  const { forestOwnerId, pageSize, continuationToken } = params

  const response = await ApiClient.get<HistoricalPropertyDto[]>(
    `/forestowners/${forestOwnerId}/historical-properties`,
    {
      headers: {
        ...(pageSize ? { 'x-page-size': pageSize.toString() } : {}),
        ...(continuationToken ? { 'x-continuation-token': continuationToken } : {}),
      },
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const responseContinuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    entities: response.data,
    hasMore: hasMoreResults,
    continuationToken: responseContinuationToken ?? undefined,
  }
}

export interface CreatePropertyRequest {
  tenantId: string
  municipalityNumber: string
  farmNumber: string
  holdingNumber: string
  leaseNumber: string
  sectionNumber: string
  mainNumber: {
    municipalityNumber: string
    farmNumber: string
    holdingNumber: string
    supplierNumber: string
    forestOwnerGroupId?: string | null
  }
  forestOwnerId: string
  streetName: string
  houseNumber: string
  postalCode: string
  city: string
  country: string
  careOf: string
  forestQuality: {
    forestQuality: 'High' | 'Middle' | 'Low'
    protectedArea: number
  }[]
}

export async function createProperty(
  propertyData: CreatePropertyRequest,
): Promise<PropertyDetailsDto> {
  return apiPost<PropertyDetailsDto>('/properties', propertyData)
}

export interface CreatePropertyConflictResult {
  status: 'conflict'
  existingPropertyId: string
}

export interface CreatePropertySuccessResult {
  status: 'created'
  property: PropertyDetailsDto
}

export type CreatePropertyResult = CreatePropertySuccessResult | CreatePropertyConflictResult

export async function createPropertyWithConflictResult(
  propertyData: CreatePropertyRequest,
): Promise<CreatePropertyResult> {
  const response = await ApiClient.post<PropertyDetailsDto>('/properties', propertyData, {
    dontThrowOnStatuses: [409],
  })

  if (response.ok) {
    return {
      status: 'created',
      property: response.data,
    }
  }

  if (response.status === 409) {
    const locationHeader = response.headers.get('location')
    const propertyIdMatch = locationHeader
      ? /\/api\/properties\/([^/]+)/.exec(locationHeader)
      : null
    const existingPropertyId = propertyIdMatch?.[1]

    if (!existingPropertyId) {
      throw new ApiError(
        response.status,
        'Conflict response missing property location',
        response.data,
      )
    }

    return {
      status: 'conflict',
      existingPropertyId,
    }
  }

  throw new ApiError(response.status, response.statusText, response.data)
}

export interface UpdatePropertyMainNumberRequest {
  tenantId?: string
  municipalityNumber: string
  farmNumber: string
  holdingNumber: string
  forestOwnerGroupId?: string | null
}

export interface UpdatePropertyDto {
  id: string
  cadastralNumber: string
  mainNumber: MainNumberDto | null
  modifiedAt: string
  updatedFields: string[]
}

export async function updatePropertyMainNumber(
  propertyId: string,
  mainNumber: UpdatePropertyMainNumberRequest,
  etag?: string | null,
): Promise<UpdatePropertyDto> {
  return apiPatch<UpdatePropertyDto>(
    `/properties/${propertyId}`,
    {
      tenantId: mainNumber.tenantId ?? getCurrentTenant(),
      mainNumber: {
        municipalityNumber: mainNumber.municipalityNumber,
        farmNumber: mainNumber.farmNumber,
        holdingNumber: mainNumber.holdingNumber,
        forestOwnerGroupId: mainNumber.forestOwnerGroupId ?? null,
      },
    },
    { etag },
  )
}

export interface UpdatePropertyForestQualityRequest {
  tenantId?: string
  forestQuality: {
    forestQuality: 'High' | 'Middle' | 'Low'
    protectedArea: number
  }[]
}

export async function updatePropertyForestQuality(
  propertyId: string,
  request: UpdatePropertyForestQualityRequest,
  etag?: string | null,
): Promise<UpdatePropertyDto> {
  // List-backed editors have no per-property ETag; resolve it from the detail endpoint.
  const propertyEtag = etag || (await getProperty(propertyId)).etag
  if (!propertyEtag) {
    throw new Error('Cannot update property forest quality without a property ETag')
  }

  return apiPatch<UpdatePropertyDto>(
    `/properties/${propertyId}`,
    {
      tenantId: request.tenantId ?? getCurrentTenant(),
      forestQuality: request.forestQuality.map((q) => ({
        forestQuality: q.forestQuality,
        protectedArea: q.protectedArea,
      })),
    },
    { etag: propertyEtag },
  )
}

export interface CreateOwnershipRequest {
  ownershipType: 'Ownership' | 'UsageRights' | 'ExclusiveUsageRights' | 'Unverified' | 'Draft'
  documentUrls?: string[] | null
  description?: string | null
  supplierNumber?: string | null
  undocumentedVerification?: boolean
}

export interface UpdateOwnershipRequest {
  ownershipType: 'Ownership' | 'UsageRights' | 'ExclusiveUsageRights' | 'Unverified' | 'Draft'
  documents?: DocumentDto[]
  description?: string
  supplierNumber?: string
  undocumentedVerification?: boolean
}

export interface UpdateOwnershipResponse {
  id: string
  forestOwnerId: string
  forestOwnerName: string
  type: string
  documents?: { id: string; url: string }[]
  description?: string
}

export async function createPropertyOwnership(
  forestOwnerId: string,
  propertyId: string,
  ownership: CreateOwnershipRequest,
  forceTransfer?: boolean,
): Promise<string> {
  const undocumentedVerification =
    ownership.undocumentedVerification ??
    (ownership.ownershipType === 'Unverified' || ownership.ownershipType === 'Draft')

  const response = await apiPost<string>(
    `/forestowners/${forestOwnerId}/properties/${propertyId}/ownerships`,
    {
      ownershipType: ownership.ownershipType,
      documentUrls: ownership.documentUrls ?? null,
      description: ownership.description ?? null,
      supplierNumber: ownership.supplierNumber ?? null,
      undocumentedVerification,
    },
    {
      params: {
        forceTransfer: forceTransfer ?? false,
      },
    },
  )
  return response
}

export async function updatePropertyOwnership(
  forestOwnerId: string,
  propertyId: string,
  ownershipId: string,
  ownership: UpdateOwnershipRequest,
  forceTransfer?: boolean,
  etag?: string | null,
): Promise<UpdateOwnershipResponse> {
  const undocumentedVerification =
    ownership.undocumentedVerification ??
    (ownership.ownershipType === 'Unverified' || ownership.ownershipType === 'Draft')

  const documents =
    ownership.documents
      ?.filter((doc) => doc.url.trim() !== '')
      .map((doc) => ({
        id: doc.id,
        url: doc.url,
      })) ?? null

  return apiPut<UpdateOwnershipResponse>(
    `/forestowners/${forestOwnerId}/properties/${propertyId}/ownerships/${ownershipId}`,
    {
      ownershipType: ownership.ownershipType,
      documents: documents != null && documents.length > 0 ? documents : null,
      description: ownership.description ?? null,
      supplierNumber: ownership.supplierNumber ?? null,
      undocumentedVerification,
    },
    {
      params: {
        forceTransfer: forceTransfer ?? false,
      },
      etag,
    },
  )
}

export async function uploadPropertyDocument(
  forestOwnerId: string,
  propertyId: string,
  ownershipId: string,
  file: File,
): Promise<DocumentDto> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await ApiClient.post<DocumentDto>(
    `/forestowners/${forestOwnerId}/properties/${propertyId}/ownerships/${ownershipId}/documents/upload`,
    formData,
    // Remove the headers object - let the browser set Content-Type automatically
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

export interface DownloadPropertyDocumentResult {
  blob: Blob
  filename: string
}

export async function downloadPropertyDocument(
  forestOwnerId: string,
  propertyId: string,
  ownershipId: string,
  documentId: string,
  documentUrl?: string,
): Promise<DownloadPropertyDocumentResult> {
  const response = await ApiClient.get<Blob>(
    `/forestowners/${forestOwnerId}/properties/${propertyId}/ownerships/${ownershipId}/documents/${documentId}/download`,
    {
      responseType: 'blob',
    },
  )

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const contentDisposition = response.headers.get('content-disposition')
  let filename = 'document'

  if (contentDisposition) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const filenameMatch = filenameRegex.exec(contentDisposition)
    if (filenameMatch?.[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '')
      try {
        filename = decodeURIComponent(filename)
      } catch {
        // If decoding fails, use the original filename
      }
    }
  } else if (documentUrl) {
    const encodedFileName = documentUrl.split('/').pop() ?? 'document'
    try {
      filename = decodeURIComponent(encodedFileName)
    } catch {
      filename = encodedFileName
    }
  }

  return {
    blob: response.data,
    filename,
  }
}

export async function deletePropertyDocument(
  forestOwnerId: string,
  propertyId: string,
  ownershipId: string,
  documentId: string,
  etag?: string | null,
): Promise<void> {
  await ApiClient.delete(
    `/forestowners/${forestOwnerId}/properties/${propertyId}/ownerships/${ownershipId}/documents/${documentId}`,
    {
      headers: etag ? { 'If-Match': etag } : undefined,
    },
  )
}

export interface TransferPropertyOwnershipRequest {
  toForestOwnerId: string
  shouldTransferCapital: boolean
  shouldTransferExtraCapital: boolean
  extraCapitalAmountToTransfer: number
  verificationDocument: DocumentDto
  ownershipType: OwnershipType
}

export interface TransferPropertyOwnershipResponse {
  id: string
  forestOwnerId: string
  forestOwnerName: string
  type: string
}

export async function transferPropertyOwnership(
  propertyId: string,
  ownershipId: string,
  request: TransferPropertyOwnershipRequest,
): Promise<TransferPropertyOwnershipResponse> {
  return apiPost<TransferPropertyOwnershipResponse>(
    `/properties/${propertyId}/ownerships/${ownershipId}/transfer`,
    {
      toForestOwnerId: request.toForestOwnerId,
      shouldTransferCapital: request.shouldTransferCapital,
      shouldTransferExtraCapital: request.shouldTransferExtraCapital,
      extraCapitalAmountToTransfer: request.extraCapitalAmountToTransfer,
      verificationDocument: request.verificationDocument,
      ownershipType: request.ownershipType,
    },
    {
      headers: {
        'x-tenant-id': getCurrentTenant(),
      },
    },
  )
}
